import { toast as toastApi } from "sonner";
import CircularCountdown from "./CircularCountdown";
import CircularSpinner from "./CircularSpinner";
import Overlay from "./Overlay";
import { useMemo, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Transition } from "@headlessui/react";

export const toast = (toastProps) =>
  new Promise((resolve) => {
    toastApi.custom(
      (id) => (
        <Toast
          id={id}
          icon={toastProps.icon}
          title={toastProps.title}
          description={toastProps.description}
          cancel={toastProps.cancel}
          timeoutSeconds={toastProps.timeoutSeconds || null}
          pauseDisabled={toastProps.pauseDisabled}
          overlayEnabled={toastProps.overlayEnabled}
          dismissible={toastProps.dismissible}
          onResolve={resolve}
        />
      ),
      {
        dismissible: !toastProps.timeoutSeconds || toastProps.dismissible,
      }
    );
  });

function Toast(props) {
  const {
    icon: Icon,
    description,
    cancel,
    id,
    timeoutSeconds,
    pauseDisabled,
    onResolve,
    overlayEnabled,
  } = props;

  const hasTimeout = typeof timeoutSeconds === "number" && timeoutSeconds > 0;
  const totalMs = useMemo(
    () => (hasTimeout ? timeoutSeconds * 1000 : null),
    [timeoutSeconds, hasTimeout]
  );

  const [remainingMs, setRemainingMs] = useState(totalMs ?? 0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!hasTimeout) return;
    setIsPaused(false);
    setRemainingMs(totalMs ?? 0);
  }, [hasTimeout, totalMs]);

  useEffect(() => {
    if (!hasTimeout || isPaused || remainingMs <= 0) return;

    let prev = performance.now();
    const interval = setInterval(() => {
      const now = performance.now();
      const delta = now - prev;
      prev = now;

      setRemainingMs((curr) => {
        const next = Math.max(0, curr - delta);
        if (next <= 0) {
          clearInterval(interval);
          handleAccept();
        }
        return next;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [hasTimeout, isPaused, id, remainingMs]);

  useEffect(() => {
    if (hasTimeout) return;
    const timer = setTimeout(() => {
      handleAccept();
    }, 3000);
    return () => clearTimeout(timer);
  }, [hasTimeout, id]);

  const handlePointerEnter = () => {
    if (!hasTimeout || !cancel || pauseDisabled) return;
    setIsPaused(true);
  };

  const handlePointerLeave = () => {
    if (!hasTimeout || !cancel || pauseDisabled) return;
    setIsPaused(false);
  };

  const resolvedRef = useRef(false);

  const handleCancel = () => {
    if (!resolvedRef.current) {
      resolvedRef.current = true;
      try {
        onResolve?.(false);
      } finally {
        toastApi.dismiss(id);
      }
    }
  };

  const handleAccept = () => {
    if (!resolvedRef.current) {
      resolvedRef.current = true;
      try {
        onResolve?.(true);
      } finally {
        toastApi.dismiss(id);
      }
    }
  };

  return (
    <>
      {createPortal(<Overlay overlayEnabled={overlayEnabled} />, document.body)}
      <div
        className="relative z-10 flex justify-center"
        style={{ width: "var(--width)" }}
      >
        <div
          className="flex rounded-2xl bg-black items-center p-4 gap-4 select-none"
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        >
          <div className="flex items-center gap-2">
            {Icon && <Icon className="size-5 shrink-0" />}
            <p className="text-sm text-white font-semibold whitespace-nowrap">
              {description}
            </p>
          </div>

          {(cancel || hasTimeout) && (
            <div className="ml-5 shrink-0 flex items-center gap-2">
              {cancel && (
                <button
                  className="rounded py-1 text-sm font-semibold text-white"
                  onClick={handleCancel}
                >
                  {cancel.label}
                </button>
              )}
              {hasTimeout &&
                (cancel ? (
                  <CircularCountdown
                    totalMs={totalMs}
                    remainingMs={remainingMs}
                    isPaused={isPaused}
                  />
                ) : (
                  <CircularSpinner />
                ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
